import { CSSProperties, ReactNode, useState } from 'react';
import { Alert } from 'antd';
import { InfoCircleOutlined, WarningOutlined, CloseOutlined } from '@ant-design/icons';

type NoticeType = 'info' | 'warning' | 'error' | 'success';

export interface NoticeProps {
  type: NoticeType;
  message: ReactNode;
  description?: ReactNode;
  showIcon?: boolean;
  icon?: ReactNode;
  style?: CSSProperties;
  className?: string;
  action?: ReactNode;
  closable?: boolean;
  onClose?: () => void;
  'data-testid'?: string;
}

const STYLED_CONFIG = {
  info: {
    borderColor: '#2E2B81',
    bg: '#f0f0ff',
    icon: <InfoCircleOutlined style={{ color: '#2E2B81', fontSize: 14, marginTop: 1 }} />,
  },
  warning: {
    borderColor: '#D4A000',
    bg: '#fffef0',
    icon: <WarningOutlined style={{ color: '#D4A000', fontSize: 14, marginTop: 1 }} />,
  },
} as const;

export function Notice({
  type,
  message,
  description,
  showIcon = true,
  icon,
  style,
  className,
  action,
  closable,
  onClose,
  'data-testid': testId,
}: NoticeProps) {
  const [closed, setClosed] = useState(false);

  if (type === 'error' || type === 'success') {
    return (
      <Alert
        type={type}
        message={message}
        description={description}
        showIcon={showIcon}
        icon={icon}
        style={style}
        className={className}
        action={action}
        closable={closable}
        onClose={onClose}
        data-testid={testId}
      />
    );
  }

  if (closed) return null;

  const config = STYLED_CONFIG[type];

  return (
    <div
      style={{
        borderLeft: `4px solid ${config.borderColor}`,
        background: config.bg,
        padding: '10px 16px',
        borderRadius: '0 6px 6px 0',
        ...style,
      }}
      className={className}
      data-testid={testId}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        {showIcon && (
          <span style={{ flexShrink: 0 }}>
            {icon ?? config.icon}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, color: '#262626', fontSize: 14 }}>
            {message}
          </span>
          {description && (
            <div style={{ marginTop: 4, color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>
              {description}
            </div>
          )}
          {action && <div style={{ marginTop: 8 }}>{action}</div>}
        </div>
        {closable && (
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#8c8c8c',
              fontSize: 12,
              padding: '2px 4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
            onClick={() => {
              setClosed(true);
              onClose?.();
            }}
          >
            <CloseOutlined />
          </button>
        )}
      </div>
    </div>
  );
}

export default Notice;
